<template>
  <hb-basic-page
    :title="$t('item_types.title')"
    fluid
    :loading="loading"
    :extension-height="60"
    :pa-0="$vuetify.breakpoint.xsOnly"
    v-if="$isTenantTechAdmin"
  >
    <template v-if="$vuetify.breakpoint.smAndUp" v-slot:app-bar-extension>
      <v-btn @click="addType" color="primary" class="mb-3 ml-n1">
        <v-icon class="no-gradient" left>mdi-plus</v-icon>
        <span>{{ $t('item_types.create_new') }}</span>
      </v-btn>
    </template>

    <v-btn v-if="$vuetify.breakpoint.xsOnly" @click="addType" color="primary" fab fixed bottom right>
      <v-icon class="no-gradient">mdi-plus</v-icon>
    </v-btn>

    <v-col cols="12" class="mb-12">
      <div class="text--secondary caption mb-2">{{ $t('item_types.order_hint') }}</div>
      <v-card elevation="0">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th style="width: 48px"></th>
                <th>{{ $t('item_types.name') }}</th>
                <th>{{ $t('item_types.plural_name') }}</th>
                <th>{{ $t('item_types.purpose') }}</th>
                <th>{{ $t('item_types.accounting_class') }}</th>
                <th>{{ $t('item_types.features') }}</th>
                <th class="text-right">{{ $t('item_types.item_count') }}</th>
                <th></th>
              </tr>
            </thead>
            <draggable v-model="types" tag="tbody" handle=".drag-handle" @end="onReorder">
              <tr v-for="type in types" :key="type.id" :class="{ 'grey--text': !type.active }">
                <td class="drag-handle" style="cursor: move">
                  <v-icon small>mdi-drag</v-icon>
                </td>
                <td>
                  {{ type.name }}
                  <v-chip v-if="!type.active" x-small class="ml-2">{{ $t('item_types.inactive') }}</v-chip>
                </td>
                <td>{{ type.pluralName }}</td>
                <td>{{ $t('item_types.purposes.' + type.purpose) }}</td>
                <td>{{ $t('item_types.accounting_classes.' + type.accountingClass) }}</td>
                <td>
                  <v-chip v-for="feature in typeFeatures(type)" :key="feature" x-small outlined class="mr-1 my-1">
                    {{ $t('item_types.feature.' + feature) }}
                  </v-chip>
                </td>
                <td class="text-right">{{ type.itemCount }}</td>
                <td class="text-right" style="white-space: nowrap">
                  <v-btn icon small @click="editType(type)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="openRemoveDialog(type)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </draggable>
          </template>
        </v-simple-table>
      </v-card>

      <confirm-dialog ref="removeDialog" />
      <item-type-dialog
        ref="itemTypeDialog"
        @created="notifyAndReload($t('item_types.created'))"
        @updated="notifyAndReload($t('item_types.updated'))"
      />
    </v-col>
  </hb-basic-page>
</template>

<script>
import draggable from 'vuedraggable';
import itemTypeApi from '@/api/itemType';
import ConfirmDialog from '@/components/ConfirmDialog';
import ItemTypeDialog from '@/components/ItemTypes/ItemTypeDialog';
import { FEATURES, loadItemTypes } from '@/helpers/itemTypes';

/**
 * Nimiketyyppien hallinta Tekniselle ylläpidolle. Tyypin tunniste tulee automaattisesti; toiminnot katsovat
 * käyttötarkoitusta ja ominaisuuksia. Muutokset päivittävät storen tyypit (muilla käyttäjillä sivun latauksessa).
 */
export default {
  name: 'ItemTypesView',
  components: { ConfirmDialog, ItemTypeDialog, draggable },
  data() {
    return {
      loading: true,
      types: [],
    };
  },
  methods: {
    typeFeatures(type) {
      return FEATURES.filter(feature => type[feature]);
    },
    addType() {
      this.$refs.itemTypeDialog.open(null);
    },
    editType(type) {
      this.$refs.itemTypeDialog.open(type);
    },
    openRemoveDialog(type) {
      this.$refs.removeDialog
        .open(this.$t('item_types.delete_dialog.title'), this.$t('item_types.delete_dialog.description', { name: type.name }), {
          color: 'error',
        })
        .then(confirm => {
          if (confirm === true) this.removeType(type);
        });
    },
    async removeType(type) {
      try {
        await itemTypeApi.removeItemType(type.id);
        await this.notifyAndReload(this.$t('item_types.deleted'));
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.$showErrorNotification(this.$t('item_types.errors.in_use'));
        } else {
          this.$handleApiError(err);
        }
      }
    },
    async notifyAndReload(message) {
      this.$showSuccessNotification(message);
      await this.reloadTypes();
    },
    async reloadTypes() {
      try {
        this.types = await itemTypeApi.getItemTypesAdmin();
        await loadItemTypes(true);
      } catch (err) {
        this.$handleApiError(err);
      }
    },
    async onReorder() {
      try {
        this.types = await itemTypeApi.reorderItemTypes(this.types.map(type => type.id));
        await loadItemTypes(true);
      } catch (err) {
        this.$handleApiError(err);
        await this.reloadTypes();
      }
    },
  },
  async mounted() {
    this.loading = true;
    await this.reloadTypes();
    this.loading = false;
  },
};
</script>
